import { type IDetectedBarcode, Scanner } from '@yudiel/react-qr-scanner'
import {
	ArrowLeftRight,
	Check,
	IdCard,
	QrCode,
	RefreshCw,
	ScanLine,
	Smartphone,
	X,
} from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Badge } from '@/components/ui/badge'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog'
import { useCameraPermission } from '@/hooks/use-camera-permission'
import { useDeviceType } from '@/hooks/useDeviceType'
import { type BadgeScanMode, VIRTUAL_BADGE_QR_PREFIX } from '@/types'
import { getCameraErrorMessage } from '@/utils/camera-error'
import { Button } from '../ui/button'
import { CameraGate, CameraStatusDot } from './camera-permission'

interface QRCodeButtonProps {
	handler: (qrCode: string, mode: BadgeScanMode) => void
	resetScan: () => void
	scannedCodes: string[]
	multiscan?: boolean
	disabled?: boolean
	className?: string
}

export const QRCodeButton = ({
	handler,
	resetScan,
	scannedCodes,
	multiscan = false,
	disabled = false,
	className,
}: QRCodeButtonProps) => {
	const { t } = useTranslation()
	const { isMobile } = useDeviceType()
	const camera = useCameraPermission()
	const [open, setOpen] = useState(false)
	const [mode, setMode] = useState<BadgeScanMode | null>(null)
	const lastScannedRef = useRef<string | null>(null)
	const scanLock = useRef(false)
	const [cameraError, setCameraError] = useState(false)
	const [scannerKey, setScannerKey] = useState(0)

	const handleQRCode = (scan: IDetectedBarcode[]) => {
		if (!scan || scan.length === 0 || !scan[0].rawValue) return

		const code = scan[0].rawValue

		if (scanLock.current) return
		if (lastScannedRef.current === code) return

		scanLock.current = true
		lastScannedRef.current = code

		if (!mode) return

		const isVirtualCode = code.startsWith(VIRTUAL_BADGE_QR_PREFIX)
		if (isVirtualCode !== (mode === 'virtual')) {
			toast.warning(
				t(
					mode === 'virtual'
						? 'qrScanner.expectedVirtualBadge'
						: 'qrScanner.expectedPhysicalBadge',
				),
			)
			releaseScanLock()
			return
		}

		// Se não for multiscan, apenas executa o handler e fecha o modal
		if (!multiscan) {
			handler(code, mode)
			handleOpenChange(false)
			return
		}

		handler(code, mode)

		releaseScanLock()
	}

	function releaseScanLock() {
		setTimeout(() => {
			scanLock.current = false
			lastScannedRef.current = null
		}, 1500)
	}

	function handleOpenChange(isOpen: boolean) {
		setOpen(isOpen)
		setCameraError(false)
		if (isOpen) camera.refresh()
		else camera.release()
		if (!isOpen) {
			setMode(null)
			scanLock.current = false
			lastScannedRef.current = null
		}
	}

	const modeOptions = [
		{
			mode: 'physical' as const,
			icon: IdCard,
			label: t('qrScanner.physicalBadge'),
			hint: t('qrScanner.physicalBadgeHint'),
		},
		{
			mode: 'virtual' as const,
			icon: Smartphone,
			label: t('qrScanner.virtualBadge'),
			hint: t('qrScanner.virtualBadgeHint'),
		},
	]

	const renderModeSelection = () => (
		<div className="mb-2 flex flex-col gap-4 p-2">
			<p className="text-center font-medium text-kaefer-dark-grey text-sm">
				{t('qrScanner.chooseMode')}
			</p>
			<div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2">
				{modeOptions.map(({ mode: option, icon: Icon, label, hint }) => (
					<button
						className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-kaefer-light-silver bg-white px-3 py-5 text-center text-kaefer-dark-grey transition-all hover:border-kaefer-medium-grey hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:scale-[0.98]"
						key={option}
						onClick={() => setMode(option)}
						type="button"
					>
						<span className="flex size-12 items-center justify-center rounded-full bg-kaefer-honey-yellow/15 text-kaefer-pumpkin-orange">
							<Icon className="size-6" />
						</span>
						<span className="font-semibold text-sm">{label}</span>
						<span className="text-muted-foreground text-xs leading-snug">
							{hint}
						</span>
					</button>
				))}
			</div>
		</div>
	)

	const renderModeSwitch = () =>
		mode && (
			<div className="flex items-center justify-between gap-2 px-1 text-sm">
				<Badge className="gap-1.5" variant="outline">
					{mode === 'virtual' ? (
						<Smartphone className="size-3.5" />
					) : (
						<IdCard className="size-3.5" />
					)}
					{mode === 'virtual'
						? t('qrScanner.virtualBadge')
						: t('qrScanner.physicalBadge')}
				</Badge>
				<button
					className="inline-flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 font-medium text-kaefer-sapphire-blue hover:bg-kaefer-sapphire-blue/10"
					onClick={() => setMode(mode === 'virtual' ? 'physical' : 'virtual')}
					type="button"
				>
					<ArrowLeftRight className="size-3.5" />
					{t('qrScanner.switchMode')}
				</button>
			</div>
		)

	const renderContent = () =>
		!mode ? (
			renderModeSelection()
		) : (
			<div className="mb-2 flex flex-col gap-2">
				{renderModeSwitch()}
				{/* Scanner sempre visível no modo multiscan */}
				<div className="flex justify-center p-2">
					<CameraGate camera={camera}>
						<Scanner
							allowMultiple
							constraints={{ facingMode: 'environment' }}
							key={scannerKey}
							onError={(error) => {
								toast.error(getCameraErrorMessage(error, t), {
									autoClose: 10000,
								})
								setCameraError(true)
							}}
							onScan={(scan) => handleQRCode(scan)}
							scanDelay={2000}
							styles={
								isMobile
									? {
											container: {
												width: '80vh',
												height: '80vh',
												maxWidth: '300px',
												maxHeight: '300px',
												aspectRatio: '1/1',
												borderRadius: '8px',
												border: '1px solid hsl(var(--border))',
											},
											video: {
												width: '100%',
												height: '100%',
												objectFit: 'cover',
												borderRadius: '6px',
											},
										}
									: {}
							}
						/>
					</CameraGate>
				</div>

				{cameraError && (
					<Button
						className="mx-auto gap-2"
						onClick={() => {
							setCameraError(false)
							setScannerKey((key) => key + 1)
						}}
						type="button"
						variant="outline"
					>
						<RefreshCw className="h-4 w-4" />
						{t('qrScanner.retryCamera')}
					</Button>
				)}

				{/* Resumo dos códigos escaneados (multiscan) */}
				{multiscan && scannedCodes.length > 0 && (
					<div className="space-y-2">
						<div className="flex items-center justify-center">
							<Badge className="text-center" variant="secondary">
								{scannedCodes.length}{' '}
								{scannedCodes.length === 1
									? t('qrScanner.scannedItem')
									: t('qrScanner.scannedItems')}
							</Badge>
						</div>
					</div>
				)}
			</div>
		)

	const renderFooter = () => (
		<div className="flex w-full gap-2">
			{multiscan && scannedCodes.length > 0 && (
				<Button
					className="flex-1 gap-2"
					onClick={resetScan}
					type="button"
					variant="outline"
				>
					<RefreshCw className="h-4 w-4" />
					{t('qrScanner.clearAll')}
				</Button>
			)}
			<Button
				className={
					multiscan && scannedCodes.length > 0 ? 'flex-1 gap-2' : 'w-full gap-2'
				}
				onClick={() => handleOpenChange(false)}
				type="button"
				variant={multiscan && scannedCodes.length > 0 ? 'default' : 'outline'}
			>
				{multiscan && scannedCodes.length > 0 ? (
					<Check className="h-4 w-4" />
				) : (
					<X className="h-4 w-4" />
				)}
				{multiscan && scannedCodes.length > 0
					? t('qrScanner.completed')
					: t('qrScanner.cancel')}
			</Button>
		</div>
	)

	return (
		<Dialog onOpenChange={handleOpenChange} open={open}>
			<DialogTrigger asChild>
				<Button
					className={`gap-2 border-2 border-dashed border-kaefer-honey-yellow/60 bg-kaefer-honey-yellow/5 text-kaefer-honey-yellow hover:border-kaefer-honey-yellow hover:bg-kaefer-honey-yellow/10 hover:text-kaefer-pumpkin-orange ${className || ''}`}
					disabled={disabled}
					size="lg"
					variant="outline"
				>
					<ScanLine className="size-5" />
					<span className="font-medium">{t('dds.scanBadge')}</span>
					<CameraStatusDot state={camera.state} />
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<QrCode className="h-5 w-5" />
						{multiscan
							? t('qrScanner.multipleScan')
							: t('qrScanner.singleScan')}
					</DialogTitle>
					<div className="text-muted-foreground text-sm">
						{scannedCodes.length > 0 && multiscan
							? t('qrScanner.codeScanned', { count: scannedCodes.length })
							: t('qrScanner.positionQR')}
					</div>
				</DialogHeader>

				{renderContent()}
				<DialogFooter>{renderFooter()}</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
